import { ref, computed, watch, onActivated, onDeactivated, type Ref } from 'vue'
import {
  LIGHTING_TARGET,
  type KeyboardLightingTarget,
  type lightingColor,
  type LightingEffectDefinition,
  type RGBGroup,
  type keyMapLighting,
} from '@rapoo/device-sdk'
import { useKeyLighting } from './useKeyLighting'
import { useSideLighting } from './useSideLighting'
import { useIndicatorLighting } from './useIndicatorLighting'
import { useAudioRhythmLighting } from './useAudioRhythmLighting'
import { useLightingCapability } from './useLightingSDK'

/**
 * 灯光页编排入口。
 *
 * - 组合 key / side / indicator / audioRhythm 四个领域 hook（autoInit: false）
 * - Tab 切换时分发 init / set*
 * - 编辑区投影「当前 Tab」状态；虚拟键盘绑按键灯 + 指示灯，音乐律动 Tab 改绑律动帧
 */

type LightTarget = KeyboardLightingTarget

function projectRef<T>(getSource: () => Ref<T>) {
  return computed({
    get: () => getSource().value,
    set: (value: T) => {
      getSource().value = value
    },
  })
}

export function useLighting() {
  const activeLightType = ref<LightTarget>(LIGHTING_TARGET.Keyboard)
  const { currentDevice, lightingCapability } = useLightingCapability()

  const key = useKeyLighting({ autoInit: false })
  const side = useSideLighting({ autoInit: false })
  const indicator = useIndicatorLighting({ autoInit: false })
  const audioRhythm = useAudioRhythmLighting({ autoInit: false })

  /** ColorPicker 在侧灯/指示灯下仍会绑 rgbGroup 等，用占位避免写穿按键灯 */
  const idleRgbGroup = ref<RGBGroup>([
    { r: 0, g: 0, b: 0 },
    { r: 255, g: 255, b: 255 },
  ])
  const idleGradientDirection = ref(1)
  const idleDirection = ref(0)
  const idleIsColorful = ref(false)
  const idleSpeed = ref(60)

  const isLoading = computed(
    () =>
      key.isLoading.value ||
      side.isLoading.value ||
      indicator.isLoading.value ||
      audioRhythm.isLoading.value,
  )

  const isKeyboard = computed(() => activeLightType.value === LIGHTING_TARGET.Keyboard)
  const isSide = computed(() => activeLightType.value === LIGHTING_TARGET.Side)
  const isIndicator = computed(() => activeLightType.value === LIGHTING_TARGET.Indicator)
  const isAudioRhythm = computed(() => activeLightType.value === LIGHTING_TARGET.AudioRhythm)

  // —— 当前 Tab 编辑态投影 ——

  const effects = projectRef(() => {
    if (isSide.value) return side.effects
    if (isIndicator.value) return indicator.effects
    if (isAudioRhythm.value) return audioRhythm.effects
    return key.effects
  })

  const currentMode = projectRef(() => {
    if (isSide.value) return side.currentMode
    if (isIndicator.value) return indicator.currentMode
    if (isAudioRhythm.value) return audioRhythm.currentMode
    return key.currentMode
  })

  const currentEffectInfo = computed(() => {
    if (isSide.value) return side.currentEffectInfo.value
    if (isIndicator.value) return indicator.currentEffectInfo.value
    if (isAudioRhythm.value) return audioRhythm.currentEffectInfo.value
    return key.currentEffectInfo.value
  })

  const audioRhythmColorId = projectRef(() => audioRhythm.colorId)
  const audioRhythmSettings = projectRef(() => audioRhythm.settings)

  const rgb = projectRef(() => {
    if (isSide.value) return side.rgb
    if (isIndicator.value) return indicator.rgb
    return key.rgb
  })

  const brightness = projectRef(() => {
    if (isSide.value) return side.brightness
    if (isIndicator.value) return indicator.brightness
    return key.brightness
  })

  const speed = projectRef(() => {
    if (isSide.value) return side.speed
    if (isIndicator.value) return idleSpeed
    return key.speed
  })

  /** 当前 Tab 灯效的速度调节范围（档位设备如 GCOME_AP 为 0-4）。 */
  const speedRange = computed(() => {
    if (isSide.value) return side.speedRange.value
    if (isIndicator.value) return { min: 0, max: 100, step: 1 }
    return key.speedRange.value
  })

  /** 当前 Tab 灯效的亮度调节范围（GCOME_AP 为 0-100 步进 25 的 5 档）。 */
  const brightnessRange = computed(() => {
    if (isSide.value) return side.brightnessRange.value
    if (isIndicator.value) return { min: 0, max: 100, step: 1 }
    return key.brightnessRange.value
  })

  const isColorful = projectRef(() => {
    if (isSide.value) return side.isColorful
    if (isIndicator.value) return idleIsColorful
    return key.isColorful
  })

  const rgbGroup = projectRef(() => (isKeyboard.value ? key.rgbGroup : idleRgbGroup))
  const gradientDirection = projectRef(() =>
    isKeyboard.value ? key.gradientDirection : idleGradientDirection,
  )
  const direction = projectRef(() => (isKeyboard.value ? key.direction : idleDirection))

  // —— 虚拟键盘：始终按键灯状态 + 指示灯部件 ——

  const keyboardRgb = key.rgb
  const keyboardRgbGroup = key.rgbGroup
  const keyboardIsColorful = key.isColorful
  const keyboardGradientDirection = key.gradientDirection
  const keyboardKeyMapRGB = computed(() =>
    isAudioRhythm.value ? audioRhythm.keyMapRGB.value : key.keyMapRGB.value,
  )
  const keyboardEffectInfo = key.currentEffectInfo
  const indicatorWidgetStyles = indicator.indicatorWidgetStyles

  // —— 生命周期 ——

  onDeactivated(() => {
    audioRhythm.stopCapture()
    key.pauseHardwareSync()
  })

  onActivated(() => {
    if (!isAudioRhythm.value) key.resumeHardwareSync()
  })

  const initTarget = async (type: LightTarget, opts?: { force?: boolean }) => {
    if (type === LIGHTING_TARGET.Side) {
      await side.init(opts)
      return
    }
    if (type === LIGHTING_TARGET.Indicator) {
      await indicator.init(opts)
      return
    }
    if (type === LIGHTING_TARGET.AudioRhythm) {
      await audioRhythm.init(opts)
      return
    }
    await key.init(opts)
    if (indicator.hasIndicator.value && !indicator.indicatorPreviewRgb.value) {
      await indicator.hydratePreview()
    }
  }

  const setLightType = async (type: LightTarget) => {
    if (type === activeLightType.value && effects.value.length > 0) return
    if (isAudioRhythm.value && type !== LIGHTING_TARGET.AudioRhythm) {
      audioRhythm.stopCapture()
      key.resumeHardwareSync()
    }
    // 进入音乐律动 Tab 即挂起按键灯硬件同步（测试页根本没有这条监听）。
    // 不要等到点「开始」：切 Tab 时若仍挂着 EffectChanged→GetLedDefine，会和后续 0xdd 抢通道。
    if (type === LIGHTING_TARGET.AudioRhythm) {
      key.pauseHardwareSync()
    }
    activeLightType.value = type
    await initTarget(type, { force: true })
  }

  watch(
    () => currentDevice.value,
    (device) => {
      audioRhythm.stopCapture()
      key.resumeHardwareSync()
      if (!device) return
      activeLightType.value = LIGHTING_TARGET.Keyboard
      void initTarget(LIGHTING_TARGET.Keyboard, { force: true })
    },
    { immediate: true },
  )

  // —— set* 分发 ——

  const setMode = async (mode?: LightingEffectDefinition) => {
    if (isSide.value) return side.setMode(mode)
    if (isIndicator.value) return indicator.setMode(mode)
    if (isAudioRhythm.value) return audioRhythm.setMode(mode)
    return key.setMode(mode)
  }

  const setColor = async (color: lightingColor, selectedKeys: keyMapLighting = []) => {
    if (isSide.value) return side.setColor(color)
    if (isIndicator.value) return indicator.setColor(color)
    return key.setColor(color, selectedKeys)
  }

  const setBrightness = async (value?: number) => {
    if (isSide.value) return side.setBrightness(value)
    if (isIndicator.value) return indicator.setBrightness(value)
    return key.setBrightness(value)
  }

  const setSpeed = async (value?: number) => {
    if (isSide.value) return side.setSpeed(value)
    if (isIndicator.value) return
    return key.setSpeed(value)
  }

  const setGradient = async (data: { rgbGroup: RGBGroup; direction: number }) => {
    if (!isKeyboard.value) return
    return key.setGradient(data)
  }

  const setDirection = async (value: number) => {
    if (!isKeyboard.value) return
    return key.setDirection(value)
  }

  const setCustomMode = async (
    mode: { event: string; type: number; name: string },
    index?: number,
  ) => {
    if (!isKeyboard.value) return
    return key.setCustomMode(mode, index)
  }

  const setBacklightEnabled = (enable: boolean) => key.setBacklightEnabled(enable)
  const setSideEnabled = (enable: boolean) => side.setSideEnabled(enable)

  return {
    isLoading,
    activeLightType,

    lightingCapability,
    hasBacklightSwitch: key.hasBacklightSwitch,
    hasSideLighting: side.hasSideLighting,
    hasIndicator: indicator.hasIndicator,
    hasAudioRhythm: audioRhythm.hasAudioRhythm,
    enabledBacklight: key.enabledBacklight,
    enabledSideLighting: side.enabledSideLighting,

    // 编辑区（当前 Tab）
    effects,
    currentMode,
    currentEffectInfo,
    rgb,
    rgbGroup,
    isColorful,
    brightness,
    speed,
    speedRange,
    brightnessRange,
    gradientDirection,
    direction,
    audioRhythmColorId,
    audioRhythmSettings,
    audioRhythmCaptureStatus: audioRhythm.captureStatus,
    audioRhythmCaptureUsingMic: audioRhythm.captureUsingMic,
    toggleAudioRhythmCapture: async () => {
      // 进入律动 Tab 时已 pause；这里再 pause 一次保证从其它路径启动也安全。
      const starting =
        audioRhythm.captureStatus.value !== 'active' &&
        audioRhythm.captureStatus.value !== 'requesting' &&
        !audioRhythm.captureRunning.value
      if (starting) key.pauseHardwareSync()
      try {
        await audioRhythm.toggleCapture()
      } finally {
        const capturing =
          audioRhythm.captureStatus.value === 'active' ||
          audioRhythm.captureStatus.value === 'requesting' ||
          audioRhythm.captureRunning.value
        // 仍在律动 Tab 时保持挂起；只有离开 Tab / 换设备才 resume（见 setLightType / device watch）。
        if (!capturing && !isAudioRhythm.value) key.resumeHardwareSync()
      }
    },

    // 虚拟键盘
    keyboardRgb,
    keyboardRgbGroup,
    keyboardIsColorful,
    keyboardGradientDirection,
    keyboardKeyMapRGB,
    keyboardEffectInfo,
    indicatorWidgetStyles,
    indicatorPreviewRgb: indicator.indicatorPreviewRgb,

    setLightType,
    setMode,
    setColor,
    setBrightness,
    setSpeed,
    setGradient,
    setDirection,
    setCustomMode,
    setBacklightEnabled,
    setSideEnabled,
  }
}
